import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken, getUser, isAuthenticated, logout } from '../utils/auth';
import AppNav from '../components/AppNav';
import './PlanSelection.css';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export default function AffiliatePage() {
  const navigate = useNavigate();
  const user = getUser();
  const token = getAuthToken();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('referrals');
  const [joining, setJoining] = useState(false);
  const [joinErr, setJoinErr] = useState('');

  const [payoutDetails, setPayoutDetails] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsMsg, setDetailsMsg] = useState('');

  const [payoutAmount, setPayoutAmount] = useState('');
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [payoutMsg, setPayoutMsg] = useState('');

  const [copied, setCopied] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/affiliate/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      setData(json);
      if (json.affiliate?.payout_details) setPayoutDetails(json.affiliate.payout_details);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    load();
  }, [load, navigate]);

  const handleJoin = async () => {
    setJoining(true);
    setJoinErr('');
    try {
      const res = await fetch(`${API_URL}/api/affiliate/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      await load();
    } catch (e) {
      setJoinErr(e.message);
    } finally {
      setJoining(false);
    }
  };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    setDetailsMsg('');
    try {
      const res = await fetch(`${API_URL}/api/affiliate/payout-details`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ payoutDetails })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      setDetailsMsg('Saved!');
      await load();
    } catch (e) {
      setDetailsMsg(`Error: ${e.message}`);
    } finally {
      setSavingDetails(false);
    }
  };

  const handleRequestPayout = async () => {
    const amt = parseFloat(payoutAmount);
    if (!amt || amt <= 0) { setPayoutMsg('Enter a valid amount'); return; }
    const minThreshold = data?.minPayoutThreshold || 0;
    if (minThreshold > 0 && amt < minThreshold) { setPayoutMsg(`Minimum payout amount is ${fmt(minThreshold)}`); return; }
    setRequestingPayout(true);
    setPayoutMsg('');
    try {
      const res = await fetch(`${API_URL}/api/affiliate/request-payout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      setPayoutMsg('Payout requested! We\'ll process it shortly.');
      setPayoutAmount('');
      await load();
    } catch (e) {
      setPayoutMsg(`Error: ${e.message}`);
    } finally {
      setRequestingPayout(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  if (!isAuthenticated()) return null;

  if (loading) return (
    <div className="checkout-container-new">
      <AppNav onLogout={handleLogout} />
      <div className="plan-selection-container" style={{ textAlign: 'center', paddingTop: '120px' }}>
        <p style={{ color: '#888', fontSize: '15px' }}>Loading...</p>
      </div>
    </div>
  );

  const affiliateLink = data?.affiliate
    ? `${window.location.origin}/?ref=${data.affiliate.affiliate_code}`
    : '';

  const maskEmail = (email) => {
    if (!email) return '—';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    return local.slice(0, 2) + '***@' + domain;
  };

  // Icon helper — renders a FontAwesome icon in a small teal-tinted circle
  const iconCircle = (iconClass, color = '#40E0D0', bg = 'rgba(64, 224, 208, 0.1)') => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: '40px', height: '40px', borderRadius: '10px',
      background: bg, color: color, fontSize: '16px', flexShrink: 0,
    }}>
      <i className={iconClass} />
    </span>
  );

  // ─── NOT ENROLLED ─── Guided onboarding
  if (!data?.enrolled) {
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={handleLogout} />
        <div className="plan-selection-container">

          {/* Hero section */}
          <div style={{ textAlign: 'center', maxWidth: '680px', margin: '0 auto', paddingTop: '40px' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '16px', margin: '0 auto 20px auto',
              background: 'linear-gradient(135deg, rgba(64, 224, 208, 0.15) 0%, rgba(32, 178, 170, 0.08) 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(64, 224, 208, 0.2)',
            }}>
              <i className="fa-solid fa-hand-holding-dollar" style={{ fontSize: '28px', color: '#40E0D0' }} />
            </div>
            <h1 style={{ fontSize: '36px', fontWeight: '700', color: '#ffffff', margin: '0 0 16px 0', letterSpacing: '-0.5px' }}>
              Earn Money by Sharing
            </h1>
            <p style={{ fontSize: '17px', color: '#999', margin: '0 0 48px 0', lineHeight: '1.7' }}>
              Share your personal link with friends, followers, or your audience.
              When they sign up and subscribe, <strong style={{ color: '#40E0D0' }}>you earn a commission on every payment they make</strong> — not just the first one.
            </p>
          </div>

          {/* How it works — 3 steps */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', maxWidth: '780px', margin: '0 auto 48px auto' }}>
            {[
              { step: '1', icon: 'fa-solid fa-link', title: 'Get Your Link', desc: 'Join the program and get a unique referral link you can share anywhere — social media, email, your website.' },
              { step: '2', icon: 'fa-solid fa-user-plus', title: 'Someone Signs Up', desc: 'When someone clicks your link and creates an account, they\'re automatically linked to you.' },
              { step: '3', icon: 'fa-solid fa-arrow-trend-up', title: 'You Earn Recurring', desc: 'Every time they pay their subscription, you earn a commission. As long as they stay subscribed, you keep earning.' },
            ].map(s => (
              <div key={s.step} style={{
                background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
                border: '1px solid #3d3d3d',
                borderRadius: '16px',
                padding: '32px 24px',
                textAlign: 'center',
                position: 'relative',
              }}>
                <div style={{
                  position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #40E0D0 0%, #20B2AA 100%)',
                  color: '#1a1a1a', width: '28px', height: '28px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: '800',
                }}>
                  {s.step}
                </div>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '12px', margin: '4px auto 16px auto',
                  background: 'rgba(64, 224, 208, 0.08)', border: '1px solid rgba(64, 224, 208, 0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className={s.icon} style={{ fontSize: '20px', color: '#40E0D0' }} />
                </div>
                <h3 style={{ margin: '0 0 8px 0', color: '#fff', fontSize: '16px', fontWeight: '700' }}>{s.title}</h3>
                <p style={{ margin: 0, color: '#999', fontSize: '13px', lineHeight: '1.6' }}>{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Commission rates table */}
          {data?.planCommissions?.length > 0 && (() => {
            const grouped = {};
            (data.planCommissions || []).forEach(p => {
              if (!grouped[p.serviceKey]) grouped[p.serviceKey] = { name: p.serviceName, plans: [] };
              grouped[p.serviceKey].plans.push(p);
            });
            return (
              <div style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                border: '1px solid rgba(64, 224, 208, 0.15)',
                borderRadius: '16px',
                padding: '28px',
                maxWidth: '580px',
                margin: '0 auto 24px auto',
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#fff', fontSize: '16px', fontWeight: '700', textAlign: 'center' }}>
                  <i className="fa-solid fa-percent" style={{ color: '#40E0D0', marginRight: '8px' }} />
                  Commission Rates
                </h3>
                {Object.values(grouped).map((svc, si) => (
                  <div key={si} style={{ marginBottom: si < Object.values(grouped).length - 1 ? '20px' : 0 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '11px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{svc.name}</p>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', fontSize: '11px', color: '#555', fontWeight: '600', paddingBottom: '6px', textTransform: 'uppercase' }}>Plan</th>
                          <th style={{ textAlign: 'center', fontSize: '11px', color: '#555', fontWeight: '600', paddingBottom: '6px', textTransform: 'uppercase' }}>Monthly</th>
                          <th style={{ textAlign: 'right', fontSize: '11px', color: '#555', fontWeight: '600', paddingBottom: '6px', textTransform: 'uppercase' }}>Your Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {svc.plans.map((p, pi) => (
                          <tr key={pi}>
                            <td style={{ padding: '7px 0', fontSize: '13px', color: '#e0e0e0', borderTop: '1px solid #2d2d2d' }}>{p.name}</td>
                            <td style={{ padding: '7px 0', fontSize: '13px', color: '#888', borderTop: '1px solid #2d2d2d', textAlign: 'center' }}>${Number(p.price || 0).toFixed(0)}/mo</td>
                            <td style={{ padding: '7px 0', fontSize: '14px', fontWeight: '700', color: '#40E0D0', borderTop: '1px solid #2d2d2d', textAlign: 'right' }}>{Number(p.commissionRate || 0).toFixed(0)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
                {data.minPayoutThreshold > 0 && (
                  <p style={{ margin: '16px 0 0 0', fontSize: '12px', color: '#666', textAlign: 'center', borderTop: '1px solid #333', paddingTop: '12px' }}>
                    <i className="fa-solid fa-circle-info" style={{ marginRight: '5px', color: '#555' }} />
                    Minimum payout request: <strong style={{ color: '#888' }}>${Number(data.minPayoutThreshold).toFixed(2)}</strong>
                  </p>
                )}
              </div>
            );
          })()}

          {/* FAQ-style details */}
          <div style={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            border: '1px solid rgba(64, 224, 208, 0.15)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '580px',
            margin: '0 auto 40px auto',
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#fff', fontSize: '16px', fontWeight: '700', textAlign: 'center' }}>
              <i className="fa-solid fa-circle-info" style={{ color: '#40E0D0', marginRight: '8px' }} />
              Good to Know
            </h3>
            {[
              { q: 'How much can I earn?', a: `Commission rates vary by plan — see the table above. Commissions are recurring so your income grows as referrals stay subscribed.`, icon: 'fa-solid fa-percent' },
              { q: 'When do I get paid?', a: 'You can request a payout anytime once your earnings are approved. We process payouts manually via bank transfer or Wise.', icon: 'fa-solid fa-calendar-check' },
              { q: 'Is there a limit?', a: 'No limits. The more people you refer, the more you earn. And since it\'s recurring, your income grows over time.', icon: 'fa-solid fa-infinity' },
              { q: 'What if someone cancels?', a: 'If the person you referred cancels their subscription, you simply stop earning from that referral. No clawbacks.', icon: 'fa-solid fa-shield-halved' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: i < 3 ? '16px' : 0, paddingBottom: i < 3 ? '16px' : 0, borderBottom: i < 3 ? '1px solid #3d3d3d' : 'none' }}>
                <i className={item.icon} style={{ color: '#555', fontSize: '13px', marginTop: '2px', width: '16px', textAlign: 'center', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: '0 0 4px 0', color: '#e0e0e0', fontSize: '13px', fontWeight: '600' }}>{item.q}</p>
                  <p style={{ margin: 0, color: '#888', fontSize: '13px', lineHeight: '1.5' }}>{item.a}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div style={{ textAlign: 'center', paddingBottom: '60px' }}>
            {joinErr && <p style={{ color: '#f87171', marginBottom: '16px', fontSize: '14px' }}>{joinErr}</p>}
            <button
              onClick={handleJoin}
              disabled={joining}
              className="btn-plan-select"
              style={{ padding: '16px 48px', fontSize: '16px', borderRadius: '12px', width: 'auto', minWidth: '260px', display: 'inline-block' }}
            >
              {joining ? 'Setting up...' : 'Start Earning Now'}
            </button>
            <p style={{ color: '#555', fontSize: '12px', marginTop: '12px' }}>Free to join. No obligations.</p>
          </div>

        </div>
      </div>
    );
  }

  // ─── ENROLLED DASHBOARD ───
  return (
    <div className="checkout-container-new">
      <AppNav onLogout={handleLogout} />
      <div className="plan-selection-container">

        {/* Page header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#ffffff', margin: '0 0 6px 0' }}>
            Your Earnings
          </h1>
          <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>
            Share your link, refer new users, and earn recurring commissions on every payment they make.
          </p>
        </div>

        {/* Referral link card */}
        <div style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          border: '1px solid rgba(64, 224, 208, 0.2)',
          borderRadius: '16px',
          padding: '24px 28px',
          marginBottom: '28px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(135deg, rgba(64, 224, 208, 0.03) 0%, transparent 100%)',
            pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <i className="fa-solid fa-link" style={{ color: '#40E0D0', fontSize: '12px' }} />
              <span style={{ color: '#888', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                Your Referral Link
              </span>
            </div>
            <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '12px' }}>
              Share this link anywhere. When someone signs up through it, they'll be linked to you automatically.
            </p>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{
                flex: 1, background: '#222', padding: '12px 16px', borderRadius: '8px',
                color: '#40E0D0', fontSize: '13px', wordBreak: 'break-all',
                border: '1px solid #333', fontFamily: 'monospace',
              }}>
                {affiliateLink}
              </code>
              <button
                onClick={() => copyLink(affiliateLink)}
                className="btn-plan-select"
                style={{ padding: '12px 24px', fontSize: '14px', borderRadius: '8px', whiteSpace: 'nowrap', minWidth: '120px' }}
              >
                <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`} style={{ marginRight: '6px' }} />
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          {[
            { label: 'Pending Review', value: fmt(data.pendingBalance), icon: 'fa-solid fa-clock', color: '#fbbf24', borderColor: 'rgba(245, 158, 11, 0.3)', iconBg: 'rgba(245, 158, 11, 0.1)' },
            { label: 'Ready to Withdraw', value: fmt(data.approvedBalance), icon: 'fa-solid fa-circle-check', color: '#4ade80', borderColor: 'rgba(34, 197, 94, 0.3)', iconBg: 'rgba(34, 197, 94, 0.1)' },
            { label: 'Total Paid Out', value: fmt(data.totalPaid), icon: 'fa-solid fa-wallet', color: '#40E0D0', borderColor: 'rgba(64, 224, 208, 0.3)', iconBg: 'rgba(64, 224, 208, 0.1)' },
          ].map(c => (
            <div key={c.label} style={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
              border: `1px solid ${c.borderColor}`,
              borderRadius: '12px',
              padding: '20px 24px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: c.iconBg, color: c.color, fontSize: '13px',
                }}>
                  <i className={c.icon} />
                </span>
                <span style={{ color: '#888', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c.label}</span>
              </div>
              <p style={{ margin: 0, fontSize: '26px', fontWeight: '800', color: '#ffffff' }}>{c.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="subscription-tabs">
          {[
            { key: 'referrals', label: 'People Referred', icon: 'fa-solid fa-users' },
            { key: 'commissions', label: 'Earnings History', icon: 'fa-solid fa-chart-line' },
            { key: 'withdraw', label: 'Withdraw', icon: 'fa-solid fa-money-bill-transfer' },
            { key: 'history', label: 'Payouts', icon: 'fa-solid fa-receipt' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`tab-btn${tab === t.key ? ' active' : ''}`}
            >
              <i className={t.icon} style={{ marginRight: '6px', fontSize: '12px' }} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Referrals tab */}
        {tab === 'referrals' && (
          <div>
            {data.referrals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '14px', margin: '0 auto 16px auto',
                  background: 'rgba(64, 224, 208, 0.06)', border: '1px solid rgba(64, 224, 208, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="fa-solid fa-user-group" style={{ fontSize: '22px', color: '#555' }} />
                </div>
                <p style={{ color: '#888', fontSize: '15px', margin: '0 0 4px 0' }}>No referrals yet</p>
                <p style={{ color: '#555', fontSize: '13px', margin: 0 }}>Share your link above to start earning!</p>
              </div>
            ) : (
              <div className="subscriptions-list">
                {data.referrals.map(r => (
                  <div key={r.id} className="subscription-card">
                    <div className="subscription-card-header">
                      <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: '36px', height: '36px', borderRadius: '8px',
                          background: 'rgba(64, 224, 208, 0.08)', color: '#40E0D0', fontSize: '14px', flexShrink: 0,
                        }}>
                          <i className="fa-solid fa-user" />
                        </span>
                        <div>
                          <h3 className="subscription-card-plan" style={{ fontSize: '15px' }}>{r.referred_email}</h3>
                          <p className="subscription-card-domain">{r.plan_name || r.plan_slug || 'No plan yet'}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className={`status-badge ${
                          r.status === 'approved' || r.status === 'paid' ? 'active' :
                          r.status === 'pending' ? 'pending' :
                          r.status === 'signup' ? '' :
                          'suspended'
                        }`}>
                          {r.status === 'signup' ? 'Signed Up' :
                           r.status === 'pending' ? 'Under Review' :
                           r.status === 'approved' ? 'Approved' :
                           r.status === 'paid' ? 'Paid' :
                           r.status === 'cancelled' ? 'Cancelled' : r.status}
                        </span>
                        {r.commission_active === 1 && (
                          <span className="status-badge active">
                            <i className="fa-solid fa-rotate" style={{ marginRight: '4px', fontSize: '10px' }} />
                            Recurring
                          </span>
                        )}
                        {r.stop_reason && (
                          <span className="status-badge suspended" title={r.stop_reason}>
                            <i className="fa-solid fa-circle-stop" style={{ marginRight: '4px', fontSize: '10px' }} />
                            Stopped
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="subscription-card-body">
                      <p className="subscription-card-billing">
                        <i className="fa-solid fa-percent" style={{ marginRight: '5px', fontSize: '10px', color: '#555' }} />
                        {r.commission_rate_locked || r.commission_rate
                          ? `${r.commission_rate_locked || r.commission_rate}% commission rate`
                          : 'Commission rate will be set when they subscribe'
                        }
                        <span style={{ margin: '0 8px', color: '#3d3d3d' }}>|</span>
                        <i className="fa-regular fa-calendar" style={{ marginRight: '5px', fontSize: '10px', color: '#555' }} />
                        {r.created_at ? `Referred ${fmtDate(r.created_at)}` : '—'}
                      </p>
                      {r.stop_reason && (
                        <p style={{ color: '#f87171', fontSize: '12px', marginTop: '6px' }}>
                          <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '5px', fontSize: '10px' }} />
                          Recurring stopped: {r.stop_reason.replace(/_/g, ' ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Commissions / Earnings History tab */}
        {tab === 'commissions' && (
          <div>
            {(!data.commissions || data.commissions.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '14px', margin: '0 auto 16px auto',
                  background: 'rgba(64, 224, 208, 0.06)', border: '1px solid rgba(64, 224, 208, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="fa-solid fa-chart-bar" style={{ fontSize: '22px', color: '#555' }} />
                </div>
                <p style={{ color: '#888', fontSize: '15px', margin: '0 0 4px 0' }}>No earnings yet</p>
                <p style={{ color: '#555', fontSize: '13px', margin: 0 }}>Earnings appear here each time a referred user makes a payment.</p>
              </div>
            ) : (
              <div className="subscriptions-list">
                {data.commissions.map(c => (
                  <div key={c.id} className="subscription-card">
                    <div className="subscription-card-header">
                      <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: '36px', height: '36px', borderRadius: '8px',
                          background: c.status === 'approved' ? 'rgba(34, 197, 94, 0.1)' :
                                     c.status === 'paid' ? 'rgba(64, 224, 208, 0.1)' :
                                     c.status === 'rejected' ? 'rgba(239, 68, 68, 0.1)' :
                                     'rgba(245, 158, 11, 0.1)',
                          color: c.status === 'approved' ? '#4ade80' :
                                 c.status === 'paid' ? '#40E0D0' :
                                 c.status === 'rejected' ? '#f87171' :
                                 '#fbbf24',
                          fontSize: '14px', flexShrink: 0,
                        }}>
                          <i className={
                            c.status === 'approved' ? 'fa-solid fa-circle-check' :
                            c.status === 'paid' ? 'fa-solid fa-check-double' :
                            c.status === 'rejected' ? 'fa-solid fa-circle-xmark' :
                            'fa-solid fa-clock'
                          } />
                        </span>
                        <div>
                          <h3 className="subscription-card-plan" style={{ fontSize: '15px' }}>
                            {fmt(c.commission_amount)}
                            <span style={{ color: '#888', fontSize: '12px', fontWeight: '400', marginLeft: '8px' }}>
                              ({c.commission_rate}% of {fmt(c.amount)})
                            </span>
                          </h3>
                          <p className="subscription-card-domain">{c.plan_name || c.plan_slug || '—'}</p>
                        </div>
                      </div>
                      <span className={`status-badge ${
                        c.status === 'approved' || c.status === 'paid' ? 'active' :
                        c.status === 'pending' ? 'pending' :
                        'suspended'
                      }`}>
                        {c.status === 'pending' ? 'Under Review' :
                         c.status === 'approved' ? 'Available' :
                         c.status === 'paid' ? 'Paid Out' :
                         c.status === 'rejected' ? 'Rejected' : c.status}
                      </span>
                    </div>
                    <div className="subscription-card-body">
                      <p className="subscription-card-billing">
                        <i className="fa-solid fa-user" style={{ marginRight: '5px', fontSize: '10px', color: '#555' }} />
                        From {maskEmail(c.referred_email)}
                        <span style={{ margin: '0 8px', color: '#3d3d3d' }}>|</span>
                        <i className="fa-regular fa-calendar" style={{ marginRight: '5px', fontSize: '10px', color: '#555' }} />
                        {fmtDate(c.created_at)}
                        {c.billing_period_start && (
                          <>
                            <span style={{ margin: '0 8px', color: '#3d3d3d' }}>|</span>
                            <i className="fa-solid fa-arrows-left-right" style={{ marginRight: '5px', fontSize: '10px', color: '#555' }} />
                            {fmtDate(c.billing_period_start)} – {fmtDate(c.billing_period_end)}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Withdraw tab */}
        {tab === 'withdraw' && (
          <div style={{ maxWidth: '560px' }}>
            {/* Payout details */}
            <div style={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
              border: '1px solid #3d3d3d',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <i className="fa-solid fa-building-columns" style={{ color: '#40E0D0', fontSize: '14px' }} />
                <h3 style={{ margin: 0, color: '#fff', fontSize: '15px', fontWeight: '700' }}>Payment Details</h3>
              </div>
              <p style={{ margin: '0 0 16px 0', color: '#888', fontSize: '13px' }}>
                How should we send your money? Enter your Wise email, PayPal, or bank details.
              </p>
              <textarea
                value={payoutDetails}
                onChange={e => setPayoutDetails(e.target.value)}
                rows={3}
                placeholder="e.g. jane@example.com (Wise) or IBAN: GB29 NWBK ..."
                style={{
                  width: '100%', padding: '12px 14px', background: '#222', border: '1px solid #333',
                  borderRadius: '8px', color: '#fff', fontSize: '14px', resize: 'vertical',
                  boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '12px' }}>
                <button
                  onClick={handleSaveDetails}
                  disabled={savingDetails}
                  className="btn-plan-select"
                  style={{ padding: '10px 24px', fontSize: '14px', borderRadius: '8px' }}
                >
                  <i className="fa-solid fa-floppy-disk" style={{ marginRight: '6px', fontSize: '12px' }} />
                  {savingDetails ? 'Saving...' : 'Save Details'}
                </button>
                {detailsMsg && <span style={{ color: detailsMsg.startsWith('Error') ? '#f87171' : '#4ade80', fontSize: '13px' }}>{detailsMsg}</span>}
              </div>
            </div>

            {/* Request payout */}
            <div style={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
              border: '1px solid #3d3d3d',
              borderRadius: '12px',
              padding: '24px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <i className="fa-solid fa-money-bill-transfer" style={{ color: '#40E0D0', fontSize: '14px' }} />
                <h3 style={{ margin: 0, color: '#fff', fontSize: '15px', fontWeight: '700' }}>Request Payout</h3>
              </div>
              <p style={{ margin: '0 0 8px 0', color: '#888', fontSize: '13px' }}>
                Available balance: <strong style={{ color: '#4ade80' }}>{fmt(data.approvedBalance)}</strong>
              </p>
              {data.minPayoutThreshold > 0 && (
                <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '12px' }}>
                  <i className="fa-solid fa-circle-info" style={{ marginRight: '4px', fontSize: '11px' }} />
                  Minimum payout: <strong style={{ color: '#888' }}>{fmt(data.minPayoutThreshold)}</strong>
                </p>
              )}
              {!data.affiliate?.payout_details && (
                <div style={{
                  background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)',
                  borderLeft: '3px solid #f59e0b', borderRadius: '8px', padding: '10px 14px',
                  color: '#fbbf24', fontSize: '13px', marginBottom: '16px',
                }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '6px', fontSize: '11px' }} />
                  Please save your payment details above before requesting a payout.
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: '#222', border: '1px solid #333', borderRadius: '8px', padding: '10px 14px',
                }}>
                  <span style={{ color: '#888' }}>$</span>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    max={data.approvedBalance}
                    value={payoutAmount}
                    onChange={e => setPayoutAmount(e.target.value)}
                    placeholder={data.approvedBalance > 0 ? data.approvedBalance.toFixed(2) : '0.00'}
                    style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '14px', width: '120px', outline: 'none' }}
                  />
                </div>
                <button
                  onClick={handleRequestPayout}
                  disabled={requestingPayout || !data.affiliate?.payout_details || data.approvedBalance <= 0 || (data.minPayoutThreshold > 0 && data.approvedBalance < data.minPayoutThreshold)}
                  className="btn-plan-select"
                  style={{
                    padding: '12px 24px', fontSize: '14px', borderRadius: '8px',
                    opacity: data.approvedBalance > 0 && data.affiliate?.payout_details && (data.minPayoutThreshold <= 0 || data.approvedBalance >= data.minPayoutThreshold) ? 1 : 0.4,
                    cursor: data.approvedBalance > 0 && data.affiliate?.payout_details && (data.minPayoutThreshold <= 0 || data.approvedBalance >= data.minPayoutThreshold) ? 'pointer' : 'not-allowed',
                  }}
                >
                  <i className="fa-solid fa-paper-plane" style={{ marginRight: '6px', fontSize: '12px' }} />
                  {requestingPayout ? 'Requesting...' : 'Request Payout'}
                </button>
              </div>
              {payoutMsg && (
                <p style={{
                  marginTop: '12px', fontSize: '13px',
                  color: payoutMsg.startsWith('Error') ? '#f87171' : '#4ade80',
                }}>{payoutMsg}</p>
              )}
            </div>
          </div>
        )}

        {/* Payouts History tab */}
        {tab === 'history' && (
          <div>
            {data.payouts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '14px', margin: '0 auto 16px auto',
                  background: 'rgba(64, 224, 208, 0.06)', border: '1px solid rgba(64, 224, 208, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="fa-solid fa-receipt" style={{ fontSize: '22px', color: '#555' }} />
                </div>
                <p style={{ color: '#888', fontSize: '15px', margin: '0 0 4px 0' }}>No payouts yet</p>
                <p style={{ color: '#555', fontSize: '13px', margin: 0 }}>Once you request a payout, it will appear here.</p>
              </div>
            ) : (
              <div className="subscriptions-list">
                {data.payouts.map(p => (
                  <div key={p.id} className="subscription-card">
                    <div className="subscription-card-header">
                      <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: '36px', height: '36px', borderRadius: '8px',
                          background: p.status === 'paid' ? 'rgba(34, 197, 94, 0.1)' :
                                     p.status === 'rejected' ? 'rgba(239, 68, 68, 0.1)' :
                                     'rgba(245, 158, 11, 0.1)',
                          color: p.status === 'paid' ? '#4ade80' :
                                 p.status === 'rejected' ? '#f87171' :
                                 '#fbbf24',
                          fontSize: '14px', flexShrink: 0,
                        }}>
                          <i className={
                            p.status === 'paid' ? 'fa-solid fa-circle-check' :
                            p.status === 'rejected' ? 'fa-solid fa-circle-xmark' :
                            'fa-solid fa-hourglass-half'
                          } />
                        </span>
                        <div>
                          <h3 className="subscription-card-plan" style={{ fontSize: '15px' }}>{fmt(p.amount)}</h3>
                          <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>
                            <i className="fa-regular fa-calendar" style={{ marginRight: '5px', fontSize: '10px' }} />
                            Requested {fmtDate(p.requested_at)}
                          </p>
                        </div>
                      </div>
                      <span className={`status-badge ${
                        p.status === 'paid' ? 'active' :
                        p.status === 'requested' ? 'pending' :
                        'suspended'
                      }`}>
                        {p.status === 'requested' ? 'Processing' :
                         p.status === 'paid' ? 'Paid' :
                         p.status === 'rejected' ? 'Rejected' : p.status}
                      </span>
                    </div>
                    {(p.note || p.paid_at) && (
                      <div className="subscription-card-body">
                        <p className="subscription-card-billing">
                          {p.paid_at && (
                            <>
                              <i className="fa-solid fa-check" style={{ marginRight: '5px', fontSize: '10px', color: '#4ade80' }} />
                              Paid on {fmtDate(p.paid_at)}
                            </>
                          )}
                          {p.note && (
                            <>
                              {p.paid_at && <span style={{ margin: '0 8px', color: '#3d3d3d' }}>|</span>}
                              <i className="fa-solid fa-message" style={{ marginRight: '5px', fontSize: '10px', color: '#555' }} />
                              {p.note}
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
